import NiceModal, { useModal } from '@ebay/nice-modal-react'
import Button from 'components/form/button'
import Input from 'components/form/input'
import Modal from 'components/modal'
import { privateRequest } from 'config/axios.config'
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useMutation, useQueryClient } from 'react-query'
import { errorHandler } from 'utils/errorHandler'

interface Payload {
  name: string
}

interface UpdatePayload extends Payload {
  id: string
}

export default NiceModal.create(
  ({ investigation, title }: { investigation?: Investigation; title: string }) => {
    const modal = useModal()
    const queryClient = useQueryClient()

    const [form, setForm] = useState({
      name: '',
    })

    const [errors, setErrors] = useState<{
      name?: string
      type?: string
      company_name?: string
    }>({})

    /* ---------------- ADD ---------------- */
    const addmedication = useMutation<{ message: string }, Error, Payload>(
      async (payload) => {
        try {
          const res = await privateRequest.post('admin/investigation', payload)
          return res.data
        } catch (error) {
          errorHandler(error)
          throw error
        }
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('investigation')
          modal.remove()
        },
      },
    )

    /* ---------------- UPDATE ---------------- */
    const updatemedication = useMutation<{ message: string }, Error, UpdatePayload>(
      async (payload) => {
        try {
          const res = await privateRequest.patch('admin/investigation', payload)
          return res.data
        } catch (error) {
          errorHandler(error)
          throw error
        }
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('investigation')
          modal.remove()
        },
      },
    )

    /* ---------------- PREFILL ON EDIT ---------------- */
    useEffect(() => {
      if (!investigation) return

      setForm({
        name: investigation.name ?? '',
      })
    }, [investigation])

    /* ---------------- CHANGE HANDLER ---------------- */
    const changeHandler = ({ target: { name, value } }: _ChangeHandlerEvent) => {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }))

      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }))
    }

    /* ---------------- SUBMIT ---------------- */
    const submitHandler = (e: _FormSubmitEvent) => {
      e.preventDefault()

      if (!form.name) {
        setErrors({
          name: !form.name ? 'Name is required' : '',
        })
        return
      }

      const payload: Payload = {
        name: form.name,
      }

      if (investigation?._id) {
        toast.promise(updatemedication.mutateAsync({ ...payload, id: investigation._id }), {
          loading: 'Updating...',
          success: (res) => res.message ?? 'Updated successfully',
          error: (err) => err.message ?? 'Something went wrong',
        })
      } else {
        toast.promise(addmedication.mutateAsync(payload), {
          loading: 'Adding...',
          success: (res) => res.message ?? 'Added successfully',
          error: (err) => err.message ?? 'Something went wrong',
        })
      }
    }

    return (
      <Modal
        title={title}
        visible={modal.visible}
        onCancel={() => modal.remove()}
        className='max-w-2xl'
      >
        <form onSubmit={submitHandler} className='flex flex-col gap-6'>
          <Input
            name='name'
            label='Name'
            placeholder="Enter Investigation Name"
            value={form.name}
            onChange={changeHandler}
            error={!!errors.name}
            helpText={errors.name}
          />

          <Button fullWidth disabled={addmedication.isLoading || updatemedication.isLoading}>
            Submit
          </Button>
        </form>
      </Modal>
    )
  },
)
